import {
  Component,
  computed,
  effect,
  input,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';

import { StaffApiService, SupervisorApiService } from '../../core/api/services';
import {
  PatientPlaylistModel,
  PatientProgramModel,
  PatientProgramStatus,
  PatientTargetModel,
  PatientTargetStatus,
  SkillAreaModel,
  ProgramOrderModel,
  SkillAcquisitionObjectiveModel,
} from '../../core/api/models';
import { SupervisorHelperService } from '../supervisor.helper.service';
import { SupervisorBaseComponent } from '../supervisor-base.component';
import { AlertService } from '../../shared/services/alert.service';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { Utils } from '../../core/utils';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { DropdownFilterService } from '../../shared/services/dropdown-filter.service';
import { DateRangeModel } from '../../shared/models/date-range-model';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { PatientDataFacadeService } from '../../core/services/patient-data-facade.service';
import {
  ACTIVE_TARGET_FILTER_STATUSES,
  filterProgramsForActiveQuickFilter,
} from '../../shared/utils/utils';
import {
  sortItemsBySupervisorDateOrder,
  supervisorDateOrderOptions,
  SupervisorOrderMode,
  supervisorOrderModeValues,
} from '../supervisor-ordering';
import { AcademyModeService } from '../../core/services/academy-mode.service';
@Component({
  selector: 'app-programs',
  templateUrl: './programs.component.html',
  styleUrl: './programs.component.css',
  standalone: false,
})
export class ProgramsComponent extends SupervisorBaseComponent {
  patientID = input.required<string>();

  selectedSkillAreas = signal<SkillAreaModel[]>([]);
  selectedPlaylists = signal<PatientPlaylistModel[]>([]);
  selectedTargets = signal<PatientTargetModel[]>([]);

  // UI State
  filtersCollapsed = signal<boolean>(true);

  // Selection functionality
  selectingForDeletion: boolean = false;
  programIDsForDeletion: number[] = [];
  doSelectAll: boolean = false;

  possibleStatuses = signal<SelectItem[]>([
    {
      label: 'Ready',
      value: PatientProgramStatus.Ready,
    },
    {
      label: 'Baseline',
      value: PatientProgramStatus.Baseline,
    },
    {
      label: 'Acquisition',
      value: PatientProgramStatus.Aquisition,
    },
    {
      label: 'Mastered',
      value: PatientProgramStatus.Mastered,
    },
    {
      label: 'Maintenance',
      value: PatientProgramStatus.Maintenance,
    },
    {
      label: 'Closed',
      value: PatientProgramStatus.Closed,
    },
    {
      label: 'On Hold',
      value: PatientProgramStatus.On_Hold,
    },
  ]);

  orderOptions = signal<SelectItem[]>([
    { label: 'Alphabetically', value: 'alphabetical' },
    { label: 'by Status', value: 'status' },
    { label: 'Custom', value: 'custom' },
    ...supervisorDateOrderOptions,
  ]);

  selectedStatuses = signal<PatientProgramStatus[]>([]);

  searchKeyword = signal<string | null>(null);
  searchTargetKeyword = signal<string | null>(null);

  includeDeleted = signal<boolean>(false);
  includeArchived = signal<boolean>(false);
  activeOnly = signal<boolean>(false);

  filterByOutOfPlaylists = signal<boolean>(false);

  // Order functionality
  orderMode = signal<SupervisorOrderMode>('alphabetical');
  isEditMode = signal<boolean>(false);
  pendingOrderChanges = signal<PatientProgramModel[]>([]);
  lastMovedProgramId = signal<number | null>(null);

  isExpandedStatus: { [key: number]: boolean } = {};
  private isProgramsDataLoading = false;

  openedDateRangeList = signal<SelectItem[]>([]);
  openedDateRange = signal<DateRangeModel | null>(null);
  openedStartDate = signal<string | null>(null);
  openedEndDate = signal<string | null>(null);
  isCustomOpenedDateRange = signal<boolean>(false);

  probedDateRangeList = signal<SelectItem[]>([]);
  probedDateRange = signal<DateRangeModel | null>(null);
  probedStartDate = signal<string | null>(null);
  probedEndDate = signal<string | null>(null);
  isCustomProbedDateRange = signal<boolean>(false);

  masteredDateRangeList = signal<SelectItem[]>([]);
  masteredDateRange = signal<DateRangeModel | null>(null);
  masteredStartDate = signal<string | null>(null);
  masteredEndDate = signal<string | null>(null);
  isCustomMasteredDateRange = signal<boolean>(false);

  closedDateRangeList = signal<SelectItem[]>([]);
  closedDateRange = signal<DateRangeModel | null>(null);
  closedStartDate = signal<string | null>(null);
  closedEndDate = signal<string | null>(null);
  isCustomClosedDateRange = signal<boolean>(false);

  onProgramExpandedChange(programId: number, isExpanded: boolean) {
    this.isExpandedStatus[programId] = isExpanded;
  }

  trackByProgramId(index: number, program: PatientProgramModel) {
    return program.ID;
  }

  filteredPrograms = computed(() => {
    let filteredPrograms = this.supervisorHelperService.programs();

    //Avoid loading wrong programs
    if (
      filteredPrograms &&
      filteredPrograms.length > 0 &&
      filteredPrograms[0].PatientID != Number(this.patientID())
    ) {
      if (!this.isProgramsDataLoading) {
        this.loadProgramsPageData(Number(this.patientID()));
      }
      return [];
    }

    // use keyword from search bars for filtering
    const programNameKeyword = this.searchKeyword()?.trim() || '';
    const targetNameKeyword = this.searchTargetKeyword()?.trim() || '';
    
    const targetNameMatches = (target: PatientTargetModel, keyword: string) => {
      if (target.TargetName?.toLowerCase().includes(keyword)) {
        return true;
      }
      const fullTarget = this.supervisorHelperService
        .targets()
        .find((t) => t.ID === target.ID);
      return (
        fullTarget?.SubTargets?.some((subTarget) =>
          subTarget.TargetName?.toLowerCase().includes(keyword)
        ) ?? false
      );
    };

    if (programNameKeyword !== '' && targetNameKeyword !== '') {
      // Both fields have values: find programs matching program name OR having a target matching target name
      const targetKeyword = targetNameKeyword.toLowerCase();
      filteredPrograms = filteredPrograms.filter((program) => {
        const matchesProgramName = program.ProgramName?.toLowerCase().includes(
          programNameKeyword.toLowerCase()
        ) ?? false;
        const hasMatchingTarget = program.Targets?.some((target) =>
          targetNameMatches(target, targetKeyword)
        ) ?? false;
        return matchesProgramName || hasMatchingTarget;
      });
    } else if (programNameKeyword !== '') {
      // Only program name field has value: find programs matching program name
      filteredPrograms = filteredPrograms.filter((program) =>
        program.ProgramName?.toLowerCase().includes(programNameKeyword.toLowerCase())
      );
    } else if (targetNameKeyword !== '') {
      // Only target name field has value: find programs having a target matching target name
      const targetKeyword = targetNameKeyword.toLowerCase();
      filteredPrograms = filteredPrograms.filter((program) =>
        program.Targets?.some((target) =>
          targetNameMatches(target, targetKeyword)
        ) ?? false
      );
    }

    // filter out or not the deleted, archived, mastered, inactive, and future targets. depending on the include filters.
    if (!this.includeDeleted()) {
      filteredPrograms = filteredPrograms.filter(
        (program) => !program.IsDeleted
      );
    }
    if (!this.includeArchived()) {
      filteredPrograms = filteredPrograms.filter(
        (program) => !program.IsArchived
      );
    }
    if (this.activeOnly()) {
      filteredPrograms = filterProgramsForActiveQuickFilter(filteredPrograms);
    }

    // if filter by out of playlists is true, filter out programs that are in playlists
    if (this.filterByOutOfPlaylists()) {
      const allPlaylists = this.supervisorHelperService.playlists();
      filteredPrograms = filteredPrograms.filter((program) => {
        // Check if program is in any playlist
        const isInPlaylist = allPlaylists.some((playlist) =>
          playlist.Items?.some(
            (item) => item.PatientProgramID === program.ID
          )
        );
        // Return only programs that are NOT in any playlist
        return !isInPlaylist;
      });
    }

    // check for each filter and apply accordingly

    // skill areas
    if (
      this.selectedSkillAreas() != null &&
      this.selectedSkillAreas().length > 0
    ) {
      filteredPrograms = filteredPrograms.filter((program) =>
        this.selectedSkillAreas().some(
          (skillArea) => skillArea.ID === program.SkillAreaID
        )
      );
    }

    // playlists
    if (
      this.selectedPlaylists() != null &&
      this.selectedPlaylists().length > 0
    ) {
      filteredPrograms = filteredPrograms.filter((program) =>
        //check if target is any of the playlists
        this.selectedPlaylists().some((playlist) => {
          return playlist.Items?.some((playlistItem) => {
            return (
              playlist.ID === playlist.ID &&
              playlistItem.PatientProgramID === program.ID
            );
          });
        })
      );
    }

    // targets
    if (this.selectedTargets() != null && this.selectedTargets().length > 0) {
      filteredPrograms = filteredPrograms.filter((program) =>
        this.selectedTargets().some((target) =>
          program.Targets?.some((t) => t.ID === target.ID)
        )
      );
    }

    // statuses
    if (this.selectedStatuses() != null && this.selectedStatuses().length > 0) {
      filteredPrograms = filteredPrograms.filter((program) =>
        this.selectedStatuses().some(
          (status) => status === program.ProgramStatus
        )
      );
    }

    //opened Date range
    if (this.openedStartDate() != null) {
      filteredPrograms = filteredPrograms.filter((target) =>
        Utils.dateStringIsBefore(
          this.openedStartDate(),
          target.DateOpened,
          true
        )
      );
    }
    if (this.openedEndDate() != null) {
      filteredPrograms = filteredPrograms.filter((target) =>
        Utils.dateStringIsBefore(target.DateOpened, this.openedEndDate(), true)
      );
    }

    //probed Date range
    if (this.probedStartDate() != null) {
      filteredPrograms = filteredPrograms.filter((target) =>
        Utils.dateStringIsBefore(
          this.probedStartDate(),
          target.DateBaselined,
          true
        )
      );
    }
    if (this.probedEndDate() != null) {
      filteredPrograms = filteredPrograms.filter((target) =>
        Utils.dateStringIsBefore(
          target.DateBaselined,
          this.probedEndDate(),
          true
        )
      );
    }

    //mastered Date range
    if (this.masteredStartDate() != null) {
      filteredPrograms = filteredPrograms.filter((target) =>
        Utils.dateStringIsBefore(
          this.masteredStartDate(),
          target.DateMastered,
          true
        )
      );
    }
    if (this.masteredEndDate() != null) {
      filteredPrograms = filteredPrograms.filter((target) =>
        Utils.dateStringIsBefore(
          target.DateMastered,
          this.masteredEndDate(),
          true
        )
      );
    }

    //mastered Date range
    if (this.closedStartDate() != null) {
      filteredPrograms = filteredPrograms.filter((target) =>
        Utils.dateStringIsBefore(
          this.closedStartDate(),
          target.DateClosed,
          true
        )
      );
    }
    if (this.closedEndDate() != null) {
      filteredPrograms = filteredPrograms.filter((target) =>
        Utils.dateStringIsBefore(target.DateClosed, this.closedEndDate(), true)
      );
    }

    // Apply sorting based on order mode
    const orderMode = this.orderMode();
    if (orderMode === 'alphabetical') {
      // order programs alphabetically
      filteredPrograms.sort((a, b) => {
        return a.ProgramName?.localeCompare(b.ProgramName ?? '') ?? 0;
      });
    } else if (orderMode === 'status') {
      // order programs by status
      filteredPrograms.sort((a, b) => {
        const statusOrder = [
          PatientProgramStatus.Ready,
          PatientProgramStatus.Baseline,
          PatientProgramStatus.Aquisition,
          PatientProgramStatus.Mastered,
          PatientProgramStatus.Maintenance,
          PatientProgramStatus.On_Hold,
          PatientProgramStatus.Closed,
        ];
        const aIndex = statusOrder.indexOf(
          a.ProgramStatus ?? PatientProgramStatus.Ready
        );
        const bIndex = statusOrder.indexOf(
          b.ProgramStatus ?? PatientProgramStatus.Ready
        );
        return aIndex - bIndex;
      });
    } else if (orderMode === 'custom') {
      // order programs by OrderNr, with null values at the end
      filteredPrograms.sort((a, b) => {
        const aOrder = a.OrderNr ?? Number.MAX_SAFE_INTEGER;
        const bOrder = b.OrderNr ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });
    } else {
      sortItemsBySupervisorDateOrder(
        filteredPrograms,
        orderMode,
        (program) => program.DateOpened,
        (program) => program.LastChangedDate
      );
    }

    // return filtered programs
    return filteredPrograms;
  });

  constructor(
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    private dropdownFilterService: DropdownFilterService,
    private router: Router,
    private patientDataFacadeService: PatientDataFacadeService,
    public academyModeService: AcademyModeService
  ) {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );
    effect(() => {
      this.supervisorHelperService.updateSignal();

      // Save the current expansion states before the update
      const currentExpandedStatus = { ...this.isExpandedStatus };

      if (this.patientID()) {
        if (!this.isProgramsDataLoading) {
          this.loadProgramsPageData(Number(this.patientID()), () => {
            // Restore the expansion states after data is loaded
            this.isExpandedStatus = currentExpandedStatus;
          });
        }
      } else {
        this.router.navigate(['/select-patient']);
      }
    });
  }

  ngOnInit(): void {
    // Initial data load is handled by the updateSignal effect above.
    this.loadOrderModePreference();

    this.openedDateRangeList.set(
      this.dropdownFilterService
        .getLast120DayDateRanges()
        .map((c) => ({ label: c.text, value: c }))
    );
    this.openedDateRange.set(this.openedDateRangeList()[0].value);

    this.probedDateRangeList.set(
      this.dropdownFilterService
        .getLast120DayDateRanges()
        .map((c) => ({ label: c.text, value: c }))
    );
    this.probedDateRange.set(this.probedDateRangeList()[0].value);

    this.masteredDateRangeList.set(
      this.dropdownFilterService
        .getLast120DayDateRanges()
        .map((c) => ({ label: c.text, value: c }))
    );
    this.masteredDateRange.set(this.masteredDateRangeList()[0].value);

    this.closedDateRangeList.set(
      this.dropdownFilterService
        .getLast120DayDateRanges()
        .map((c) => ({ label: c.text, value: c }))
    );
    this.closedDateRange.set(this.closedDateRangeList()[0].value);
  }

  private loadProgramsPageData(patientID: number, finishedLoading?: () => void) {
    this.isLoading = true;
    this.isProgramsDataLoading = true;

    const wrappedCalls: Observable<
      | PatientPlaylistModel[]
      | SkillAreaModel[]
      | PatientProgramModel[]
      | PatientTargetModel[]
      | SkillAcquisitionObjectiveModel[]
      | { error: true; details: unknown }
    >[] = [
      this.supervisorApiService
        .GetPatientPlaylists({ patientID })
        .pipe(catchError((error) => of({ error: true as const, details: error }))),
      this.supervisorApiService
        .GetSkillAreas({ patientID })
        .pipe(catchError((error) => of({ error: true as const, details: error }))),
      this.patientDataFacadeService
        .getPrograms(patientID, {
          onlyActive: false,
        })
        .pipe(catchError((error) => of({ error: true as const, details: error }))),
      this.patientDataFacadeService
        .getTargets(patientID, {
          onlyActive: false,
        })
        .pipe(catchError((error) => of({ error: true as const, details: error }))),
      this.supervisorApiService
        .GetPatientSkillAcquisitionObjectives({ patientID })
        .pipe(catchError((error) => of({ error: true as const, details: error }))),
    ];

    forkJoin(wrappedCalls).subscribe(
      ([playlists, skillAreas, programs, targets, skillAcquisitionGoals]) => {
        if (!('error' in playlists)) {
          this.supervisorHelperService.playlists.set(playlists as PatientPlaylistModel[]);
        }
        if (!('error' in skillAreas)) {
          this.supervisorHelperService.skillAreas.set(skillAreas as SkillAreaModel[]);
        }
        if (!('error' in programs)) {
          this.supervisorHelperService.programs.set(programs as PatientProgramModel[]);
        }
        if (!('error' in targets)) {
          this.supervisorHelperService.targets.set(targets as PatientTargetModel[]);
        }
        if (!('error' in skillAcquisitionGoals)) {
          this.supervisorHelperService.skillAcquisitionGoals.set(
            skillAcquisitionGoals as SkillAcquisitionObjectiveModel[]
          );
        }

        this.isLoading = false;
        this.isProgramsDataLoading = false;
        if (finishedLoading) {
          finishedLoading();
        }
      },
      () => {
        this.isLoading = false;
        this.isProgramsDataLoading = false;
      }
    );
  }

  nameForFilter(filter: any): string {
    if (this.supervisorHelperService.isSkillAreaModel(filter)) {
      return filter.SkillAreaName ?? 'No Name for Skill Area';
    }
    if (this.supervisorHelperService.isPatientTargetModel(filter)) {
      return filter.TargetName ?? 'No Name for Target';
    }

    return '';
  }

  searchProgram($event: AutoCompleteCompleteEvent) {
    console.log('searchProgram');
  }

  clearSearch() {
    this.searchKeyword.set(null);
    this.searchTargetKeyword.set(null);
    this.selectedSkillAreas.set([]);
    this.selectedPlaylists.set([]);
    this.selectedTargets.set([]);
    this.selectedStatuses.set([]);
    this.includeDeleted.set(false);
    this.includeArchived.set(false);
    this.activeOnly.set(false);
    this.exitEditModeIfActive();
  }

  addProgramButtonClicked() {
    console.log('addProgramButtonClicked');
    this.supervisorHelperService.additProgramButtonClicked(null);
  }

  getFromBSPButtonClicked() {
    console.log(
      this.academyModeService.getImportButtonId() + 'Clicked'
    );

    // Save the current expansion states before the update
    const currentExpandedStatus = { ...this.isExpandedStatus };

    this.supervisorApiService
      .ImportProgramsFromBSP({ patientID: Number(this.patientID()) })
      .subscribe(
        (response) => {
          console.log('ImportProgramsFromBSP response: ', response);
          Utils.showImportConclusionMessage(this.alertService, response);
          this.loadProgramsPageData(Number(this.patientID()), () => {
            // Restore the expansion states after data is loaded
            this.isExpandedStatus = currentExpandedStatus;
          });
        },
        (err) => {
          this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
        }
      );
  }

  toggleIncludeDeleted() {
    this.includeDeleted.set(!this.includeDeleted());
    this.exitEditModeIfActive();
  }

  toggleIncludeArchived() {
    this.includeArchived.set(!this.includeArchived());
    this.exitEditModeIfActive();
  }

  toggleActiveOnly() {
    this.activeOnly.set(!this.activeOnly());
    this.exitEditModeIfActive();
  }

  toggleFilterByOutOfPlaylists() {
    this.filterByOutOfPlaylists.set(!this.filterByOutOfPlaylists());
  }

  changedOpenedDateRange() {
    if (!this.openedDateRange()) {
      this.isCustomOpenedDateRange.set(false);
      this.openedStartDate.set(null);
      this.openedEndDate.set(null);
      this.exitEditModeIfActive();
      return;
    }

    if (this.openedDateRange()!.isCustom) {
      this.isCustomOpenedDateRange.set(true);
      return;
    }

    this.isCustomOpenedDateRange.set(false);
    this.openedStartDate.set(this.openedDateRange()!.startDate);
    this.openedEndDate.set(this.openedDateRange()!.endDate);
    this.exitEditModeIfActive();
  }

  changedProbedDateRange() {
    if (!this.probedDateRange()) {
      this.isCustomProbedDateRange.set(false);
      this.probedStartDate.set(null);
      this.probedEndDate.set(null);
      this.exitEditModeIfActive();
      return;
    }

    if (this.probedDateRange()!.isCustom) {
      this.isCustomProbedDateRange.set(true);
      return;
    }

    this.isCustomProbedDateRange.set(false);
    this.probedStartDate.set(this.probedDateRange()!.startDate);
    this.probedEndDate.set(this.probedDateRange()!.endDate);
    this.exitEditModeIfActive();
  }

  changedMasteredDateRange() {
    if (!this.masteredDateRange()) {
      this.isCustomMasteredDateRange.set(false);
      this.masteredStartDate.set(null);
      this.masteredEndDate.set(null);
      this.exitEditModeIfActive();
      return;
    }

    if (this.masteredDateRange()!.isCustom) {
      this.isCustomMasteredDateRange.set(true);
      return;
    }

    this.isCustomMasteredDateRange.set(false);
    this.masteredStartDate.set(this.masteredDateRange()!.startDate);
    this.masteredEndDate.set(this.masteredDateRange()!.endDate);
    this.exitEditModeIfActive();
  }

  changedClosedDateRange() {
    if (!this.closedDateRange()) {
      this.isCustomClosedDateRange.set(false);
      this.closedStartDate.set(null);
      this.closedEndDate.set(null);
      this.exitEditModeIfActive();
      return;
    }

    if (this.closedDateRange()!.isCustom) {
      this.isCustomClosedDateRange.set(true);
      return;
    }

    this.isCustomClosedDateRange.set(false);
    this.closedStartDate.set(this.closedDateRange()!.startDate);
    this.closedEndDate.set(this.closedDateRange()!.endDate);
    this.exitEditModeIfActive();
  }

  // Order functionality methods
  onOrderModeChange() {
    // Save the order mode preference
    this.saveOrderModePreference();
  }

  /**
   * Helper method to exit edit mode if active before applying filters
   */
  exitEditModeIfActive(): void {
    if (this.isEditMode()) {
      // Save any pending changes before exiting edit mode
      this.saveAllPendingChanges();
      this.isEditMode.set(false);
    }
  }

  toggleEditMode() {
    if (this.isEditMode()) {
      // Exiting edit mode - save all pending changes
      this.saveAllPendingChanges();
    } else {
      // Starting edit mode - check if filters are active
      if (this.hasActiveFilters()) {
        this.alertService
          .confirm(
            'Clear Filters',
            'To start configuring a custom order for this list we must first clear the filters, click "yes" to reset the filters and start editing, "no" to cancel'
          )
          .subscribe((result) => {
            if (result) {
              this.clearAllFilters();
              this.isEditMode.set(true);
            }
          });
        return;
      }
    }
    this.isEditMode.set(!this.isEditMode());
  }

  private hasActiveFilters(): boolean {
    return (
      (this.selectedSkillAreas() && this.selectedSkillAreas().length > 0) ||
      (this.selectedPlaylists() && this.selectedPlaylists().length > 0) ||
      (this.selectedTargets() && this.selectedTargets().length > 0) ||
      (this.selectedStatuses() && this.selectedStatuses().length > 0) ||
      (this.searchKeyword() && this.searchKeyword() !== '') ||
      (this.searchTargetKeyword() && this.searchTargetKeyword() !== '') ||
      this.includeDeleted() ||
      this.includeArchived() ||
      this.activeOnly()
    );
  }

  private clearAllFilters(): void {
    this.selectedSkillAreas.set([]);
    this.selectedPlaylists.set([]);
    this.selectedTargets.set([]);
    this.selectedStatuses.set([]);
    this.searchKeyword.set(null);
    this.searchTargetKeyword.set(null);
    this.includeDeleted.set(false);
    this.includeArchived.set(false);
    this.activeOnly.set(false);
  }

  moveProgramUp(programId: number) {
    if (this.orderMode() === 'custom') {
      const programs = this.filteredPrograms();
      const currentIndex = programs.findIndex((p) => p.ID === programId);

      if (currentIndex > 0) {
        // Track the last moved program
        this.lastMovedProgramId.set(programId);

        // Add animation to both affected cells
        this.animateCell(programId, 'moving-up');
        this.animateCell(programs[currentIndex - 1].ID!, 'moving-down');

        // Swap with the program above
        [programs[currentIndex], programs[currentIndex - 1]] = [
          programs[currentIndex - 1],
          programs[currentIndex],
        ];

        // Update OrderNr for both programs
        programs[currentIndex].OrderNr = currentIndex + 1;
        programs[currentIndex - 1].OrderNr = currentIndex;

        // Track changes for later saving
        this.trackOrderChange(programs[currentIndex]);
        this.trackOrderChange(programs[currentIndex - 1]);

        // Save the complete order immediately
        this.saveCompleteOrder();
      }
    }
  }

  moveProgramDown(programId: number) {
    if (this.orderMode() === 'custom') {
      const programs = this.filteredPrograms();
      const currentIndex = programs.findIndex((p) => p.ID === programId);

      if (currentIndex < programs.length - 1) {
        // Track the last moved program
        this.lastMovedProgramId.set(programId);

        // Add animation to both affected cells
        this.animateCell(programId, 'moving-down');
        this.animateCell(programs[currentIndex + 1].ID!, 'moving-up');

        // Swap with the program below
        [programs[currentIndex], programs[currentIndex + 1]] = [
          programs[currentIndex + 1],
          programs[currentIndex],
        ];

        // Update OrderNr for both programs
        programs[currentIndex].OrderNr = currentIndex + 1;
        programs[currentIndex + 1].OrderNr = currentIndex + 2;

        // Track changes for later saving
        this.trackOrderChange(programs[currentIndex]);
        this.trackOrderChange(programs[currentIndex + 1]);

        // Save the complete order immediately
        this.saveCompleteOrder();
      }
    }
  }

  moveProgramToTop(programId: number) {
    if (this.orderMode() === 'custom') {
      const programs = this.filteredPrograms();
      const currentIndex = programs.findIndex((p) => p.ID === programId);

      if (currentIndex > 0) {
        // Track the last moved program
        this.lastMovedProgramId.set(programId);

        // Add animation to the moving cell
        this.animateCell(programId, 'moving-up');

        // Move program to the top
        const program = programs.splice(currentIndex, 1)[0];
        programs.unshift(program);

        // Update OrderNr for all programs
        programs.forEach((p, index) => {
          p.OrderNr = index + 1;
        });

        // Track changes for all programs
        programs.forEach((p) => this.trackOrderChange(p));

        // Save the complete order immediately
        this.saveCompleteOrder();
      }
    }
  }

  moveProgramToBottom(programId: number) {
    if (this.orderMode() === 'custom') {
      const programs = this.filteredPrograms();
      const currentIndex = programs.findIndex((p) => p.ID === programId);

      if (currentIndex < programs.length - 1) {
        // Track the last moved program
        this.lastMovedProgramId.set(programId);

        // Add animation to the moving cell
        this.animateCell(programId, 'moving-down');

        // Move program to the bottom
        const program = programs.splice(currentIndex, 1)[0];
        programs.push(program);

        // Update OrderNr for all programs
        programs.forEach((p, index) => {
          p.OrderNr = index + 1;
        });

        // Track changes for all programs
        programs.forEach((p) => this.trackOrderChange(p));

        // Save the complete order immediately
        this.saveCompleteOrder();
      }
    }
  }

  getStatusLabel(status: PatientProgramStatus): string {
    const statusItem = this.possibleStatuses().find((s) => s.value === status);
    return statusItem?.label || 'Unknown';
  }

  isFirstProgram(programId: number): boolean {
    return this.filteredPrograms().findIndex((p) => p.ID === programId) === 0;
  }

  isLastProgram(programId: number): boolean {
    return (
      this.filteredPrograms().findIndex((p) => p.ID === programId) ===
      this.filteredPrograms().length - 1
    );
  }

  hasPendingChanges(programId: number): boolean {
    return this.lastMovedProgramId() === programId;
  }

  private animateCell(programId: number, animationClass: string): void {
    const element = document.getElementById(`edit-container-${programId}`);
    if (element) {
      // Remove any existing animation classes
      element.classList.remove('moving-up', 'moving-down', 'moving');

      // Add the new animation class
      element.classList.add(animationClass);

      // Remove the animation class after the animation completes
      setTimeout(() => {
        element.classList.remove(animationClass);
      }, 300); // Match the animation duration
    }
  }

  private trackOrderChange(program: PatientProgramModel): void {
    const pendingChanges = this.pendingOrderChanges();
    const existingIndex = pendingChanges.findIndex((p) => p.ID === program.ID);

    if (existingIndex >= 0) {
      // Update existing change
      pendingChanges[existingIndex] = { ...program };
    } else {
      // Add new change
      pendingChanges.push({ ...program });
    }

    this.pendingOrderChanges.set([...pendingChanges]);
  }

  private saveCompleteOrder(): void {
    // Get all programs for the current patient to save complete order
    const allPrograms = this.supervisorHelperService.programs();

    if (allPrograms.length === 0) {
      return;
    }

    // Save all programs to ensure complete order is preserved
    const updatePromises = allPrograms.map((program) => {
      return this.supervisorApiService
        .SavePatientProgram({
          body: {
            PatientProgram: {
              ...program,
              OrderNr: program.OrderNr,
            },
            PropagateStatus: false,
            PropagateArchieve: false,
          },
        })
        .toPromise();
    });

    Promise.all(updatePromises)
      .then(() => {
        console.log('Complete program order saved successfully');
        // Clear pending changes since we've saved everything
        this.pendingOrderChanges.set([]);
        // Trigger a refresh to get the updated data
        this.loadProgramsPageData(Number(this.patientID()));
      })
      .catch((error) => {
        console.error('Error saving complete program order:', error);
        this.alertService.error('Failed to save program order changes');
      });
  }

  private saveAllPendingChanges(): void {
    const pendingChanges = this.pendingOrderChanges();

    if (pendingChanges.length === 0) {
      return;
    }

    // Get all programs (not just filtered ones) to ensure proper order values for all items
    const allPrograms = this.supervisorHelperService.programs();

    // Create order dictionary with all programs
    const programOrders: { [key: string]: number | null } = {};

    // First, set order for all programs based on their current OrderNr
    allPrograms.forEach((program) => {
      if (program.OrderNr !== null && program.OrderNr !== undefined) {
        programOrders[program.ID!.toString()] = program.OrderNr;
      }
    });

    // Then update with the pending changes
    pendingChanges.forEach((program) => {
      if (program.OrderNr !== null && program.OrderNr !== undefined) {
        programOrders[program.ID!.toString()] = program.OrderNr;
      }
    });

    // Create the order model
    const orderModel: ProgramOrderModel = {
      PatientID: Number(this.patientID()),
      ProgramOrders: programOrders,
    };

    // Save all order changes in a single call
    this.supervisorApiService
      .SaveProgramOrder({
        body: orderModel,
      })
      .subscribe({
        next: () => {
          console.log('All program order changes saved successfully');
          // Clear pending changes
          this.pendingOrderChanges.set([]);
          // Trigger a refresh to get the updated data
          this.loadProgramsPageData(Number(this.patientID()));
        },
        error: (error) => {
          console.error('Error saving program order changes:', error);
          this.alertService.error('Failed to save program order changes');
        },
      });
  }

  private saveOrderModePreference(): void {
    const patientId = this.patientID();
    const orderMode = this.orderMode();
    if (patientId) {
      sessionStorage.setItem(`programs_order_mode_${patientId}`, orderMode);
    }
  }

  private loadOrderModePreference(): void {
    const patientId = this.patientID();
    if (patientId) {
      const savedOrderMode = sessionStorage.getItem(
        `programs_order_mode_${patientId}`
      ) as SupervisorOrderMode | null;
      if (savedOrderMode && supervisorOrderModeValues.includes(savedOrderMode)) {
        this.orderMode.set(savedOrderMode);
      }
    }
  }

  toggleFiltersCollapsed(): void {
    this.filtersCollapsed.set(!this.filtersCollapsed());
  }

  // Selection functionality methods
  toggleIsSelecting(): void {
    this.selectingForDeletion = !this.selectingForDeletion;
  }

  cancelIsSelecting(): void {
    this.selectingForDeletion = false;
    this.programIDsForDeletion = [];
  }

  selectAllPrograms(): void {
    this.doSelectAll = !this.doSelectAll;
    if (this.doSelectAll == false) this.programIDsForDeletion = [];
  }

  deleteSelectedPrograms(): void {
    if (this.selectingForDeletion && this.programIDsForDeletion.length > 0) {
      // Only ask for confirmation - no propagation
      this.alertService
        .confirm(
          'Confirm Deletion',
          `Are you sure you want to mark ${this.programIDsForDeletion.length} program(s) as deleted?`
        )
        .subscribe((confirmed) => {
          if (confirmed) {
            this.isLoading = true;
            this.supervisorApiService
              .MarkMultiplePatientProgramsAsDeleted({
                body: {
                  PatientID: Number(this.patientID()),
                  ProgramIDs: this.programIDsForDeletion,
                },
              })
              .subscribe({
                next: (result) => {
                  this.alertService.success('Programs marked as deleted');
                  this.supervisorHelperService.triggerUpdateSignal();
                  this.cancelIsSelecting();
                },
                complete: () => {
                  this.isLoading = false;
                },
                error: (error) => {
                  console.error(error);
                  this.alertService.error('Failed to mark programs as deleted');
                  this.isLoading = false;
                  this.supervisorHelperService.triggerUpdateSignal();
                },
              });
          }
        });
    }
  }

  selectProgram(programID: number): void {
    if (this.programIDsForDeletion.includes(programID)) {
      this.programIDsForDeletion = this.programIDsForDeletion.filter(
        (id) => id !== programID
      );
    } else {
      this.programIDsForDeletion.push(programID);
    }
    console.log(this.programIDsForDeletion);
  }

  getTotalProgramsCount(): number {
    return this.filteredPrograms().length;
  }
  convertProgramStatusToTargetStatus(
    statuses: PatientProgramStatus[]
  ): PatientTargetStatus[] {
    let targetStatuses: PatientTargetStatus[] = [];
    if (statuses && statuses.length > 0) {
      for (let status of statuses) {
        switch (status) {
          case PatientProgramStatus.Ready:
            targetStatuses.push(PatientTargetStatus.Ready);
            break;
          case PatientProgramStatus.Baseline:
            targetStatuses.push(PatientTargetStatus.Baseline);
            break;
          case PatientProgramStatus.Aquisition:
            targetStatuses.push(PatientTargetStatus.Aquisition);
            break;
          case PatientProgramStatus.Mastered:
            targetStatuses.push(PatientTargetStatus.Mastered);
            targetStatuses.push(PatientTargetStatus.Fluent);
            break;
          case PatientProgramStatus.Maintenance:
            targetStatuses.push(PatientTargetStatus.Maintenance);
            break;
          case PatientProgramStatus.Closed:
            targetStatuses.push(PatientTargetStatus.Closed);
            break;
          case PatientProgramStatus.On_Hold:
            targetStatuses.push(PatientTargetStatus.On_Hold);
            break;
          default:
            targetStatuses.push(PatientTargetStatus.Ready);
            break;
        }
      }
    }
    return targetStatuses;
  }

  targetStatusesToFilter(): PatientTargetStatus[] {
    const targetStatuses = this.convertProgramStatusToTargetStatus(
      this.selectedStatuses()
    );

    if (!this.activeOnly()) {
      return targetStatuses;
    }

    if (targetStatuses.length === 0) {
      return [...ACTIVE_TARGET_FILTER_STATUSES];
    }

    const activeStatuses = targetStatuses.filter((status) =>
      ACTIVE_TARGET_FILTER_STATUSES.includes(status)
    );

    return activeStatuses.length > 0
      ? activeStatuses
      : [...ACTIVE_TARGET_FILTER_STATUSES];
  }

}
